export enum CandidateRegistrationRequestResult {
  SUCCESS = 'SUCCESS',
  TELEPHONE_NUMBER_ALREADY_IN_USE = 'TELEPHONE_NUMBER_ALREADY_IN_USE',
  INVALID_SMS_DESTINATION_NUMBER = 'INVALID_SMS_DESTINATION_NUMBER',
}

export interface CandidateRegistrationRequestForm {
  readonly telephoneNumber: string
  readonly locale: LocaleType
  readonly timezone: string
  readonly country: string
}

export enum CandidateVerificationResult {
  SUCCESS = 'SUCCESS',
  INVALID_CODE = 'INVALID_CODE',
}

export interface CandidateVerificationForm {
  readonly telephoneNumber: string
  readonly code: string
  readonly password: string
  readonly givenName: string
  readonly familyName: string
  readonly zip: string
}

export enum CandidateWorkPreference {
  PERMANENT_EMPLOYMENT = 'PERMANENT_EMPLOYMENT',
  FREELANCE = 'FREELANCE',
  WORKING_STUDENT = 'WORKING_STUDENT',
  INTERNSHIP = 'INTERNSHIP',
  APPRENTICESHIP = 'APPRENTICESHIP',
  DUAL_STUDY_PROGRAM = 'DUAL_STUDY_PROGRAM',
  FULL_TIME = 'FULL_TIME',
  PART_TIME = 'PART_TIME',
  MINI_JOB = 'MINI_JOB',
  TEMPORARY_HELP = 'TEMPORARY_HELP',
  REMOTE = 'REMOTE',
  ONSITE = 'ONSITE',
  HYBRID = 'HYBRID',
}

export enum CandidateTelephoneNumberChangeRequestResult {
  SUCCESS = 'SUCCESS',
  TELEPHONE_NUMBER_ALREADY_IN_USE = 'TELEPHONE_NUMBER_ALREADY_IN_USE',
  INVALID_SMS_DESTINATION_NUMBER = 'INVALID_SMS_DESTINATION_NUMBER',
}

export enum CandidateTelephoneNumberChangeResult {
  SUCCESS = 'SUCCESS',
  INVALID_CODE = 'INVALID_CODE',
}

export enum CandidateEmailAddressChangeRequestResult {
  SUCCESS = 'SUCCESS',
  EMAIL_ADDRESS_ALREADY_IN_USE = 'EMAIL_ADDRESS_ALREADY_IN_USE',
}

export enum CandidateEmailAddressChangeResult {
  SUCCESS = 'SUCCESS',
  INVALID_CODE = 'INVALID_CODE',
}

export enum CandidatePasswordChangeResult {
  SUCCESS = 'SUCCESS',
  OLD_PASSWORD_INCORRECT = 'OLD_PASSWORD_INCORRECT',
}

export interface CandidatePasswordChangeForm {
  readonly oldPassword: string
  readonly newPassword: string
}

export enum CandidateGender {
  MALE = 'MALE',
  FEMALE = 'FEMALE',
  DIVERSE = 'DIVERSE',
}

export interface CandidateLoginForm {
  readonly telephoneNumber: string
  readonly password: string
}

export enum CandidateLanguageProficiencyLevel {
  ELEMENTARY = 'ELEMENTARY',
  INTERMEDIATE = 'INTERMEDIATE',
  FLUENT = 'FLUENT',
  NATIVE = 'NATIVE',
}

export interface CandidateLanguageProficiency {
  readonly language: string
  readonly proficiencyLevel: CandidateLanguageProficiencyLevel
}

export interface CandidateSkill {
  readonly id: number
  readonly skill: string
}

export interface CandidateProfile {
  readonly id: string
  readonly activated?: Date
  readonly telephoneNumber: string
  readonly emailAddress?: string
  readonly locale: LocaleType
  readonly timezone: string
  readonly namePrefix?: string
  readonly gender?: CandidateGender
  readonly givenName?: string
  readonly familyName?: string
  readonly dateOfBirth?: string
  readonly placeOfBirth?: string
  readonly address1?: string
  readonly address2?: string
  readonly zip?: string
  readonly city?: string
  readonly state?: string
  readonly country: string
  readonly citizenships: string[]
  readonly workPreferences: CandidateWorkPreference[]
  readonly profilePictureHash?: string
  readonly languageProficiencies: CandidateLanguageProficiency[]
  readonly skills: CandidateSkill[]
}

export enum CandidateCaseStatus {
  OPEN = 'OPEN',
  POSTPONED = 'POSTPONED',
  CLOSED = 'CLOSED',
}

export interface Candidate {
  readonly id: string
  readonly created: Date
  readonly updated: Date
  readonly accessed?: Date
  readonly activated?: Date
  readonly verificationRequestCreated?: Date
  readonly verificationRequestCode?: string
  readonly passwordResetRequestCreated?: Date
  readonly passwordResetRequestCode?: string
  readonly telephoneNumber: string
  readonly telephoneNumberChangeRequestCreated?: Date
  readonly telephoneNumberChangeRequestCode?: string
  readonly telephoneNumberChangeRequestNewTelephoneNumber?: string
  readonly emailAddress?: string
  readonly emailAddressChangeRequestCreated?: Date
  readonly emailAddressChangeRequestCode?: string
  readonly emailAddressChangeRequestNewEmailAddress?: string
  readonly passwordHash?: string
  readonly passwordSalt?: string
  readonly locale: LocaleType
  readonly timezone: string
  readonly gender?: CandidateGender
  readonly namePrefix?: string
  readonly givenName?: string
  readonly familyName?: string
  readonly dateOfBirth?: string
  readonly placeOfBirth?: string
  readonly address1?: string
  readonly address2?: string
  readonly zip?: string
  readonly city?: string
  readonly state?: string
  readonly country: string
  readonly citizenships: string[]
  readonly workPreferences: CandidateWorkPreference[]
  readonly profilePictureHash?: string
  readonly languageProficiencies: CandidateLanguageProficiency[]
  readonly skills: CandidateSkill[]
  readonly caseStatus: CandidateCaseStatus
  readonly caseClosed?: Date
  readonly casePostponedUntil?: Date
}

export interface CandidatePersonalInformationForm {
  readonly namePrefix?: string
  readonly gender?: CandidateGender
  readonly givenName: string
  readonly familyName: string
  readonly dateOfBirth?: string
  readonly placeOfBirth?: string
  readonly address1?: string
  readonly address2?: string
  readonly zip: string
  readonly city?: string
  readonly state?: string
  readonly country: string
  readonly citizenships: string[]
}

export enum CandidatePasswordResetRequestResult {
  SUCCESS = 'SUCCESS',
  TELEPHONE_NUMBER_UNKNOWN = 'TELEPHONE_NUMBER_UNKNOWN',
}

export enum CandidatePasswordResetResult {
  SUCCESS = 'SUCCESS',
  INVALID_CODE = 'INVALID_CODE',
}

export interface CandidatePasswordResetForm {
  readonly telephoneNumber: string
  readonly code: string
  readonly password: string
}

export interface CandidateNotification extends EventNotification {
  readonly candidate: Candidate
}

export default function () {
  const { t, locale } = useI18n()
  const { $api } = useNuxtApp()

  const candidateProfile = useState<CandidateProfile | undefined>('candidateProfile')

  async function submitCandidateRegistrationRequest(candidateRegistrationRequestForm: CandidateRegistrationRequestForm, eventId: string): Promise<CandidateRegistrationRequestResult> {
    return await $api('/api/candidate', {
      method: 'POST',
      body: candidateRegistrationRequestForm,
      headers: { 'x-event-id': eventId },
    })
  }

  async function submitCandidateVerification(candidateVerificationForm: CandidateVerificationForm, eventId: string): Promise<CandidateVerificationResult> {
    return await $api('/api/candidate/_verify', {
      method: 'POST',
      body: candidateVerificationForm,
      headers: { 'x-event-id': eventId },
    })
  }

  async function candidateLogin(candidateLoginForm: CandidateLoginForm): Promise<boolean> {
    try {
      candidateProfile.value = await $api('/api/candidate/_login', {
        method: 'POST',
        body: candidateLoginForm,
      })
    }
    catch (e) {
      if (e instanceof FetchErrorUnauthorized) {
        return false
      }

      throw e
    }

    return true
  }

  async function candidateLogout(): Promise<void> {
    await $api('/api/candidate/_logout', {
      method: 'POST',
    })
    candidateProfile.value = undefined
  }

  async function requestPasswordReset(telephoneNumber: string): Promise<CandidatePasswordResetRequestResult> {
    return await $api('/api/candidate/_request-password-reset', {
      method: 'POST',
      params: { telephoneNumber },
    })
  }

  async function resetPassword(candidatePasswordResetForm: CandidatePasswordResetForm): Promise<CandidatePasswordResetResult> {
    return await $api('/api/candidate/_reset-password', {
      method: 'POST',
      body: candidatePasswordResetForm,
    })
  }

  async function doesTelephoneNumberExist(telephoneNumber: string): Promise<boolean> {
    return await $api('/api/candidate/_telephone-number-exists', {
      params: { telephoneNumber },
    })
  }

  async function activate(): Promise<void> {
    const result: Date = cloneObject(await $api('/api/candidate/_activate', {
      method: 'POST',
    }))

    candidateProfile.value = { ...candidateProfile.value!, activated: result }
  }

  async function deactivate() {
    await $api('/api/candidate/_deactivate', {
      method: 'POST',
    })

    candidateProfile.value = { ...candidateProfile.value!, activated: undefined }
  }

  async function updatePassword(candidatePasswordChangeForm: CandidatePasswordChangeForm): Promise<CandidatePasswordChangeResult> {
    return await $api('/api/candidate/_update-password', {
      method: 'POST',
      body: candidatePasswordChangeForm,
    })
  }

  async function requestTelephoneNumberChange(telephoneNumber: string): Promise<CandidateTelephoneNumberChangeRequestResult> {
    return await $api('/api/candidate/_request-telephone-number-change', {
      method: 'POST',
      body: JSON.stringify(telephoneNumber),
    })
  }

  async function changeTelephoneNumber(code: string): Promise<CandidateTelephoneNumberChangeResult> {
    return await $api('/api/candidate/_change-telephone-number', {
      method: 'POST',
      body: JSON.stringify(code),
    })
  }

  function updateTelephoneNumberInProfile(telephoneNumber: string) {
    candidateProfile.value = { ...candidateProfile.value!, telephoneNumber }
  }

  async function requestEmailAddressChange(emailAddress: string): Promise<CandidateEmailAddressChangeRequestResult> {
    return await $api('/api/candidate/_request-email-address-change', {
      method: 'POST',
      body: JSON.stringify(emailAddress),
    })
  }

  async function changeEmailAddress(code: string): Promise<CandidateEmailAddressChangeResult> {
    return await $api('/api/candidate/_change-email-address', {
      method: 'POST',
      body: JSON.stringify(code),
    })
  }

  function updateEmailAddressInProfile(emailAddress: string) {
    candidateProfile.value = { ...candidateProfile.value!, emailAddress }
  }

  async function updateCandidateLocale(locale: LocaleType): Promise<void> {
    await $api('/api/candidate/_update-locale', {
      method: 'POST',
      body: JSON.stringify(locale),
    })

    candidateProfile.value = { ...candidateProfile.value!, locale }
  }

  async function updateTimezone(timezone: string): Promise<void> {
    await $api('/api/candidate/_update-timezone', {
      method: 'POST',
      body: JSON.stringify(timezone),
    })

    candidateProfile.value = { ...candidateProfile.value!, timezone }
  }

  async function updatePersonalInformation(personalInformationForm: CandidatePersonalInformationForm): Promise<void> {
    await $api('/api/candidate/_update-personal-information', {
      method: 'POST',
      body: personalInformationForm,
    })

    candidateProfile.value = {
      ...candidateProfile.value!,
      namePrefix: trimAndReturnUndefinedIfEmpty(personalInformationForm.namePrefix),
      gender: personalInformationForm.gender,
      givenName: trimAndReturnUndefinedIfEmpty(personalInformationForm.givenName)!,
      familyName: trimAndReturnUndefinedIfEmpty(personalInformationForm.familyName)!,
      dateOfBirth: personalInformationForm.dateOfBirth,
      placeOfBirth: trimAndReturnUndefinedIfEmpty(personalInformationForm.placeOfBirth),
      address1: trimAndReturnUndefinedIfEmpty(personalInformationForm.address1),
      address2: trimAndReturnUndefinedIfEmpty(personalInformationForm.address2),
      zip: trimAndReturnUndefinedIfEmpty(personalInformationForm.zip)!,
      city: trimAndReturnUndefinedIfEmpty(personalInformationForm.city),
      state: personalInformationForm.state,
      country: personalInformationForm.country,
      citizenships: personalInformationForm.citizenships,
    }
  }

  async function updateWorkPreferences(workPreferences: CandidateWorkPreference[]): Promise<void> {
    await $api('/api/candidate/_update-work-preferences', {
      method: 'POST',
      body: workPreferences,
    })

    candidateProfile.value = { ...candidateProfile.value!, workPreferences }
  }

  async function updateProfilePicture(blob?: Blob): Promise<void> {
    const profilePictureHash: string = await $api('/api/candidate/_update-profile-picture', {
      method: 'POST',
      body: blob,
      headers: { 'content-type': 'application/octet-stream' },
    })

    candidateProfile.value = { ...candidateProfile.value!, profilePictureHash }
  }

  function getProfilePictureUrl(): string | undefined {
    if (!candidateProfile.value) {
      return undefined
    }

    if (!candidateProfile.value.profilePictureHash) {
      return undefined
    }

    return `/api/candidate/${candidateProfile.value.id}/${candidateProfile.value.profilePictureHash}.jpg`
  }

  async function upsertLanguageProficiency(languageProficiency: CandidateLanguageProficiency): Promise<void> {
    await $api('/api/candidate/_upsert-language-proficiency', {
      method: 'POST',
      body: languageProficiency,
    })

    const index = candidateProfile.value?.languageProficiencies.findIndex(item => item.language === languageProficiency.language)
    if (index !== -1) {
      const newLanguageProficiencies = candidateProfile.value!.languageProficiencies
      newLanguageProficiencies[index!] = languageProficiency
      candidateProfile.value = { ...candidateProfile.value!, languageProficiencies: newLanguageProficiencies }
    }
    else {
      const newLanguageProficiencies = candidateProfile.value!.languageProficiencies
      newLanguageProficiencies.push(languageProficiency)
      candidateProfile.value = { ...candidateProfile.value!, languageProficiencies: newLanguageProficiencies }
    }
  }

  async function deleteLanguageProficiency(language: string): Promise<void> {
    await $api('/api/candidate/_delete-language-proficiency', {
      method: 'POST',
      body: JSON.stringify(language),
    })

    const newLanguageProficiencies = candidateProfile.value!.languageProficiencies.filter(item => item.language !== language)
    candidateProfile.value = { ...candidateProfile.value!, languageProficiencies: newLanguageProficiencies }
  }

  async function insertSkill(skill: string): Promise<void> {
    const result: CandidateSkill = await $api('/api/candidate/_insert-skill', {
      method: 'POST',
      body: JSON.stringify(skill),
    })

    const newSkills = candidateProfile.value!.skills
    newSkills.push(result)
    candidateProfile.value = { ...candidateProfile.value!, skills: newSkills }
  }

  async function updateSkill(skillId: number, skill: string): Promise<void> {
    const result: string = await $api('/api/candidate/_update-skill', {
      method: 'POST',
      body: JSON.stringify(skill),
      params: { skillId },
    })

    const index = candidateProfile.value?.skills.findIndex(item => item.id === skillId)
    if (index !== -1) {
      const newSkills = candidateProfile.value!.skills
      newSkills[index!] = { ...newSkills[index!], skill: result }
      candidateProfile.value = { ...candidateProfile.value!, skills: newSkills }
    }
  }

  async function deleteSkill(skillId: number): Promise<void> {
    await $api('/api/candidate/_delete-skill', {
      method: 'POST',
      params: { skillId },
    })

    const newSkills = candidateProfile.value!.skills.filter(item => item.id !== skillId)
    candidateProfile.value = { ...candidateProfile.value!, skills: newSkills }
  }

  async function getAllCandidates(): Promise<Candidate[]> {
    return cloneObject(await $api('/api/candidate/_get-all-candidates'))
  }

  async function getCandidateById(id: string): Promise<Candidate> {
    return cloneObject(await $api(`/api/candidate/${id}`))
  }

  async function openCase(id: string): Promise<void> {
    await $api(`/api/candidate/${id}/_open-case`, {
      method: 'POST',
    })
  }

  async function closeCase(id: string): Promise<void> {
    await $api(`/api/candidate/${id}/_close-case`, {
      method: 'POST',
    })
  }

  async function postponeCase(id: string, postponeForSeconds: number): Promise<void> {
    await $api(`/api/candidate/${id}/_postpone-case`, {
      method: 'POST',
      body: JSON.stringify(postponeForSeconds),
    })
  }

  function sortLanguageProficiences(languageProficiencies: CandidateLanguageProficiency[]) {
    return languageProficiencies.slice().sort((a, b) => {
      const sortPriorityA = getProficiencyLevelSortPriority(a)
      const sortPriorityB = getProficiencyLevelSortPriority(b)

      if (sortPriorityA < sortPriorityB) return 1
      if (sortPriorityA > sortPriorityB) return -1

      const languageLabelA = t(`language.${a.language}`)
      const languageLabelB = t(`language.${b.language}`)

      return languageLabelA.localeCompare(languageLabelB, locale.value)
    })
  }

  function getProficiencyLevelSortPriority(candidateLanguageProficiency: CandidateLanguageProficiency): number {
    if (candidateLanguageProficiency.proficiencyLevel === CandidateLanguageProficiencyLevel.NATIVE) {
      return 3
    }
    if (candidateLanguageProficiency.proficiencyLevel === CandidateLanguageProficiencyLevel.FLUENT) {
      return 2
    }
    if (candidateLanguageProficiency.proficiencyLevel === CandidateLanguageProficiencyLevel.INTERMEDIATE) {
      return 1
    }

    return 0
  }

  return {
    submitCandidateRegistrationRequest,
    submitCandidateVerification,
    candidateLogin,
    candidateLogout,
    candidateProfile,
    requestPasswordReset,
    resetPassword,
    doesTelephoneNumberExist,
    activate,
    deactivate,
    updatePassword,
    requestTelephoneNumberChange,
    changeTelephoneNumber,
    updateTelephoneNumberInProfile,
    requestEmailAddressChange,
    changeEmailAddress,
    updateEmailAddressInProfile,
    updateCandidateLocale,
    updateTimezone,
    updatePersonalInformation,
    updateWorkPreferences,
    updateProfilePicture,
    getProfilePictureUrl,
    upsertLanguageProficiency,
    deleteLanguageProficiency,
    insertSkill,
    updateSkill,
    deleteSkill,
    getAllCandidates,
    getCandidateById,
    openCase,
    closeCase,
    postponeCase,
    sortLanguageProficiences,
  }
}
